<template>
  <div class="tw:size-full tw:flex tw:flex-col tw:text-base tw:text-body tw:@container">
    <div
      v-if="user"
      class="tw:relative tw:bg-background tw:flex tw:flex-col tw:flex-1 tw:rounded-lg tw:w-full tw:overflow-hidden tw:min-h-[500px]"
    >
      <template v-if="conversationMessages.data.length > 0">
        <QuickConversationHeader />
        <div ref="messagesRef" class="tw:overflow-auto tw:grow-1">
          <div v-if="loadingConversationMessages" class="tw:py-4 tw:text-center">
            <i class="fal fa-spin fa-spinner" />
          </div>

          <QuickConversationDefaultMessages
            v-if="showDefaultMessage"
          />

          <QuickConversationMessage
            v-for="message in conversationMessages.data"
            :key="message.id"
            :message="message"
          />

          <div
            v-if="!isMessageResponding"
            class="tw:pl-[calc(30px+1.5rem)] tw:pr-4"
          >
            <QuickConversationCandidateResponses />
          </div>
        </div>
        <div class="tw:px-4 tw:mt-1 tw:relative">
          <div
            v-show="!shouldNotShowScrollToBottom"
            class="tw:-top-[50px] tw:absolute tw:left-0 tw:right-0 tw:mx-auto tw:w-[100px] tw:text-center"
          >
            <Button
              variant="outline"
              size="icon"
              class="tw:rounded-full tw:size-7"
              @click="scrollToBottom"
            >
              <i class="fal fa-arrow-down" />
            </Button>
          </div>

          <div v-if="debugMode && spark.state.user" class="tw:flex tw:md:block tw:flex-col tw:text-center tw:relative tw:mt-2">
            <button
              type="button"
              class="btn btn-danger btn-sm btn-block tw:text-base"
              @click="resetConversation"
            >
              Reset Conversation
            </button>
          </div>
        </div>
        <QuickConversationPromptInput />
      </template>

      <QuickConversationWelcome v-else class="tw:flex-1" />
    </div>

    <p
      v-if="shouldShowCopyRight"
      class="tw:text-center tw:mt-1"
    >
      <a
        :href="siaUrl"
        target="_blank"
        class="tw:text-muted-foreground tw:text-xs"
      >
        Powered by Dubb SIA
      </a>
    </p>
  </div>
</template>

<script>
/* eslint-disable no-console */
import axios from 'axios'
import concat from 'lodash/concat'
import reverse from 'lodash/reverse'
import { DateTime } from 'luxon'
import URI from 'urijs'
import { bus } from '@/common/setup-app/event-bus.js'
import StorageKeys from '@/common/storage-keys'
import { Button } from '@/shadcn/components/ui/button/index.js'
import { convert_markdown, extract_video_canonicals } from '@/utils.js'
import QuickConversationCandidateResponses from './quick-conversation-candidate-responses.vue'
import QuickConversationDefaultMessages from './quick-conversation-default-messages.vue'
import QuickConversationHeader from './quick-conversation-header.vue'
import QuickConversationMessage from './quick-conversation-message.vue'
import QuickConversationPromptInput from './quick-conversation-prompt-input.vue'
import QuickConversationWelcome from './quick-conversation-welcome.vue'
import { useProvideQuickConversation } from './use-quick-conversation'

export default {
  name: 'QuickConversation',
  components: {
    Button,
    QuickConversationCandidateResponses,
    QuickConversationWelcome,
    QuickConversationPromptInput,
    QuickConversationMessage,
    QuickConversationDefaultMessages,
    QuickConversationHeader,
  },
  props: {
    user: {
      required: true,
    },
    defaultMessages: {
      type: Array,
      default: () => [],
    },
    candidateResponses: {
      type: Array,
      default: () => [],
    },
    video: {},
    salesAssistant: {
      required: true,
    },
    limitRequests: {
      type: Number,
    },
    shouldShowCopyRight: {
      type: Boolean,
      default: true,
    },
  },
  emits: ['trigger-limit-requests'],
  setup(props, { emit }) {
    const {
      shouldNotShowScrollToBottom,
      scrollToBottom,
      isChattingWithAi,
      userPhotoUrl,
      userDisplayName,
      composeForm,
      signUpForm,
      conversationId,
      conversationMessages,
      isMessageResponding,
      initSparkState,
      appendMessage,
    } = useProvideQuickConversation(props, emit)

    return {
      shouldNotShowScrollToBottom,
      scrollToBottom,
      isChattingWithAi,
      userPhotoUrl,
      userDisplayName,
      composeForm,
      signUpForm,
      conversationId,
      conversationMessages,
      isMessageResponding,
      initSparkState,
      appendMessage,
    }
  },
  data() {
    return {
      showDefaultMessage: false,
      defaultMessageTime: null,
      loadingConversationMessages: false,
      debugMode: false,
    }
  },
  computed: {
    siaUrl() {
      let queries = {
        utm_source: 'SiaFeaturePage',
        utm_medium: 'lp',
        utm_campaign: 'Footer',
        utm_content: 'link',
      }
      if (window.affiliateParams) {
        queries = {
          ...queries,
          ...window.affiliateParams,
        }
      }

      return URI('https://dubb.com/features/sia').addQuery(queries).toString()
    },
  },
  mounted() {
    const query = URI.parseQuery(location.search)
    if (query.email) {
      this.signUpForm.email = query.email
    }
    if (query.debug_sia) {
      this.enableDebugMode(query.debug_sia)
    }

    if (!Spark.state.user && sessionStorage.getItem(StorageKeys.TEMP_USER_STATE)) {
      this.initSparkState(JSON.parse(sessionStorage.getItem(StorageKeys.TEMP_USER_STATE)))
    }

    this.findConversation()

    bus.$on('private-channel.messaging.new-chat-message', (event) => {
      if (!event.conversation || !event.message) {
        return
      }
      if (this.conversationId && event.conversation.id === this.conversationId) {
        this.appendMessage(this.parseVideoCanonicals(event.message))
      }
    })
    bus.$on('private-channel.messaging.ai-typing-status', (event) => {
      if (this.conversationId && event.user_id === this.user.id) {
        this.conversationMessages.data = this.conversationMessages.data.filter(msg => !msg.is_typing)
        this.appendMessage({
          sender_id: this.user.id,
          is_typing: true,
          message: event.message,
          hint: event.hint,
        })

        if (!event.message && !event.hint) {
          this.scrollToBottom()
        }
      }
    })
  },
  methods: {
    convert_markdown,
    parseVideoCanonicals(message) {
      if (message.is_rich && message.message) {
        const canonicals = extract_video_canonicals(message.message)
        if (canonicals.length > 0) {
          message.video_canonicals = canonicals
        }
      }
      return message
    },
    findConversation() {
      if (!this.spark.state.user) {
        this.initDefaultMessage()

        return
      }
      axios.get(this.router('api.conversation.find', { friend: this.user.id }))
        .then((resp) => {
          if (resp.data) {
            this.conversationId = resp.data.id
            this.loadMessages(1)
          }
          else {
            this.initDefaultMessage()
          }
        })
    },

    enableDebugMode(token) {
      this.debugMode = true
      axios.interceptors.request.use((config) => {
        config.headers['X-Debug-Sia'] = token
        return config
      })
      const logMessage = (message) => {
        switch (message.type) {
          case 'message':
            if (typeof message.content === 'string') {
              console.log(`[${message.role}]${message.content}`)
            }
            else {
              message.content.forEach((msg) => {
                console.log(`[${message.role}]${msg.text}`)
              })
            }
            break
          case 'function_call':
            console.log(`[Function call]${message.name}(${message.arguments})`)
            break
          case 'function_call_output':
            console.log(`[Function call output]${message.output}`)
            break
        }
      }
      bus.$on('private-channel.messaging.ai-debug', (event) => {
        const headerFormat = 'color: blue; font-weight: bold; font-size: 14px;'
        console.log(
          `%cRequest Model: ${event.payload.request.model}, Enabled Tools: ${event.payload.request.tools.map(tool => tool.name).join(', ')}`,
          headerFormat,
        )
        event.payload.request.input.forEach((message) => {
          logMessage(message)
        })
        console.log('%cResponse: ', headerFormat)
        event.payload.response.output.forEach((message) => {
          logMessage(message)
        })
      })
    },

    initDefaultMessage() {
      this.defaultMessageTime = DateTime.now()
      this.showDefaultMessage = true
    },
    loadMessages(page = 1) {
      if (this.loadingConversationMessages) {
        return
      }
      this.loadingConversationMessages = true
      axios.get(this.router('api.conversation.messages', { conversation: this.conversationId, page }))
        .then((resp) => {
          const messages = reverse(resp.data.data).map(message => this.parseVideoCanonicals(message))

          if (page === 1) {
            this.conversationMessages.data = messages
            if (messages.length === 0 && this.defaultMessage !== '') {
              this.initDefaultMessage()
            }
          }
          else {
            this.conversationMessages.data = concat(messages, this.conversationMessages.data)
          }
          this.conversationMessages.meta = resp.data.meta
          if (page === 1) {
            this.scrollToBottom()
          }
          this.loadingConversationMessages = false
        })
    },
    resetConversation() {
      axios.post(this.router('api.conversation.reset', { friend: this.user.id }))
        .then(() => {
          location.reload()
        })
    },
  },
}
</script>
